import { TAuthConverter, TConvertor, TCreateConverter, TFetchConverter } from 'data/types/converter.types';
import { TUserPermissionsUI, TNotificationKey } from 'data/types/permissions.types';
import {
  EPropertyStatusDB,
  EPropertyStatusUI,
  EPropertiesTemplateTypeDB,
  EPropertiesTemplateTypeUI,
  EPropertySourceDB,
  EPropertySourceUI,
  TAssignPropertyDB,
  TAssignPropertyUI,
  TCreatePropertyDB,
  TCreatePropertyUI,
  TInvitePropertyManagerUI,
  TInvitePropertyMangerDB,
  TPetChargeStatusesUploadDB,
  TPetChargeStatusesUploadUI,
  TPropertiesCSVUploadResultDB,
  TPropertiesCSVUploadResultUI,
  TPropertiesDB,
  TPropertiesTypeDB,
  TPropertiesTypeUI,
  TAnimalTypeManagementDB,
  TAnimalTypeManagementUI,
  TPropertiesUI,
  TPropertiesUploadUI,
  TPropertyDB,
  TPropertyManagerPropertiesDB,
  TPropertyManagerPropertiesUI,
  TPropertyManagersTableDB,
  TPropertyManagersTableUI,
  TPropertyTypeDB,
  TPropertyTypeUI,
  TPropertyUI,
  TUnassignedPropertiesDB,
  TUnassignedPropertiesUI,
  TUnitDB,
  TUnitUI,
  TPropertyUploadedFilesUI,
  TPropertyUploadedFilesDB,
  EUploadedFileTypeUI,
  EUploadedFileTypeDB,
  TResidentsScreeningActionUI,
  TResidentsScreeningActionDB,
  TScreeningConfigurationUI,
  TScreeningConfigurationDB,
  TAddUnitsUI,
  TAddUnitsDB,
  ENotificationTypesUI,
  ENotificationTypesDB,
  TAssignedPropertiesUI,
  TAssignedPropertiesDB,
  TPropertyMetadataDB,
  TPropertyMetadataUI,
  TAssignTeamMembersDB,
  TAssignTeamMembersUI,
  TTeamMembersUploadUI,
  TTeamMembersCSVUploadResultUI,
  TTeamMembersCSVUploadResultDB,
  TAnimalUpdateConfig,
  TPropertyDnaSettingDB,
  TPropertyDnaTestingUI,
} from 'data/types/property.types';
import { EPooCompaniesDB } from 'data/types/residence.types';
import { b64toBlob } from 'helpers/fileServices';
import { dateTimeFormat, formatStateLabel, formattedDateTimezone, formatToSnakeCase } from 'helpers/utils';
import {
  userPermissionsFromDb,
  capabilitiesToDb,
  notificationsToDb,
  notificationsFromDb,
} from './permissionSets.converters';
import { selectOptionConverter } from './general.converters';
import { propertyConfigSetGetConverter } from './propertyConfigSet․converters';

export const propertyDnaSettingConverter: TConvertor<TPropertyDnaTestingUI, TPropertyDnaSettingDB> = {
  fromDb: (data) => {
    const companyOption = data?.company?.companyOption;
    return {
      isEnabled: !!data?.isEnabled,
      company:
        companyOption === EPooCompaniesDB.POO_PRINTS || companyOption === EPooCompaniesDB.MR_DOG_POOP
          ? companyOption
          : null,
    };
  },
  toDb: (data) => ({
    isEnabled: data.isEnabled,
    company: data.isEnabled && data.company ? { companyOption: data.company, companyCustomName: null } : null,
  }),
};

export const propertiesGetConverter: TFetchConverter<TPropertyUI, TPropertyDB> = {
  fromDb: (data) => {
    return {
      name: data.name,
      value: data.id,
      address1: data.address1,
    };
  },
};

/**
 * The label to show for a property in a picker.
 *
 * Property names are free text: some are the street address, some are a community name, and near-duplicates
 * ("Redwood Road" and "Redwood Road II") are common. Showing the address is what tells those apart — and it is
 * also what makes an address findable, because these options are fetched once and filtered in the browser on
 * the rendered label rather than re-queried per keystroke. A property named "Redwood Road" at 951 N Redwood Rd
 * could not be found by typing "951" until the address was part of its label (PP-10045).
 */
export const getPropertySearchLabel = (property: TPropertyUI): string =>
  property.address1 ? `${property.name} (${property.address1})` : property.name;

export const getUnitsCoverter: TFetchConverter<TUnitUI, TUnitDB> = {
  fromDb: (data) => {
    return {
      name: data.number,
      value: data.id,
    };
  },
};

export const propertyTypeConverter: TFetchConverter<TPropertyTypeUI, TPropertyTypeDB> = {
  fromDb: (data) => ({
    title: data.displayName,
    keyWord: data.key,
  }),
};

export const propertiesTemplateTypeConverter: TConvertor<EPropertiesTemplateTypeUI, EPropertiesTemplateTypeDB> = {
  fromDb: (data) => {
    const dataMapper: {
      [key in EPropertiesTemplateTypeDB]: EPropertiesTemplateTypeUI;
    } = {
      [EPropertiesTemplateTypeDB.OPP]: EPropertiesTemplateTypeUI.OPP,
    };
    return dataMapper[data];
  },
  toDb: (data) => {
    const dataMapper: {
      [key in EPropertiesTemplateTypeUI]: EPropertiesTemplateTypeDB;
    } = {
      [EPropertiesTemplateTypeUI.OPP]: EPropertiesTemplateTypeDB.OPP,
    };
    return dataMapper[data];
  },
};
export const propertyCsvUploadConverter: TAuthConverter<
  TPropertiesUploadUI,
  FormData,
  TPropertiesCSVUploadResultUI,
  TPropertiesCSVUploadResultDB
> = {
  toDb: (data) => {
    const formData = new FormData();
    formData.append('type', propertiesTemplateTypeConverter.toDb(data.type));
    data.file.forEach((item) => {
      formData.append('file', new Blob([item], { type: 'application/json' }));
    });
    if (data.shouldDeleteNotMatchedProperties) {
      const shouldDeleteNotMatchedProperties = data.shouldDeleteNotMatchedProperties === 'Yes';
      formData.append('shouldDeleteNotMatchedProperties', `${shouldDeleteNotMatchedProperties}`);
    }
    if (data.animalRequirementsSetId.value) {
      formData.append('animalVerificationConfigurationId', data.animalRequirementsSetId.value);
    }
    return formData;
  },
  fromDb: (data) => {
    let file = null;
    if (data.skippedProperties) {
      file = b64toBlob(data.skippedProperties, 'text/csv');
    }
    return {
      skippedProperties: file,
      skippedPropertiesCount: data.skippedPropertiesCount,
      removedPropertiesCount: data.removedPropertiesCount,
      addedPropertiesCount: data.addedPropertiesCount,
      matchedPropertiesCount: data.matchedPropertiesCount,
      totalPropertiesCount: data.totalPropertiesCount,
      createdAccessCodesCount: data.createdAccessCodes,
      matchedAccessCodesCount: data.matchedAccessCodes,
      notMatchedProperties: data.notMatchedProperties,
    };
  },
};
export const propertySourceConverter: TFetchConverter<EPropertySourceUI, EPropertySourceDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in EPropertySourceDB]: EPropertySourceUI } = {
      [EPropertySourceDB.RESMAN]: EPropertySourceUI.RESMAN,
      [EPropertySourceDB.SYSTEM]: EPropertySourceUI.SYSTEM,
    };
    return dataMapper[data];
  },
};

export const propertyStatusConverter: TFetchConverter<EPropertyStatusUI, EPropertyStatusDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in EPropertyStatusDB]: EPropertyStatusUI } = {
      [EPropertyStatusDB.ACTIVE]: EPropertyStatusUI.ACTIVE,
      [EPropertyStatusDB.PENDING]: EPropertyStatusUI.PENDING,
    };
    return dataMapper[data];
  },
};

export const propertiesConverter: TFetchConverter<TPropertiesUI, TPropertiesDB> = {
  fromDb: (data) => {
    return {
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      id: data.id,
      name: data.name,
      photoUrl: data.photoUrl,
      source: propertySourceConverter.fromDb(data.source),
      state: formatStateLabel(data.state),
      stateCode: data.stateCode,
      type: data.type,
      unitCount: data.unitCount,
      zip: data.zip,
      defaultPolicy: data.defaultPolicy,
      petsAllowed: data.petsAllowed,
      status: propertyStatusConverter.fromDb(data.status),
    };
  },
};

export const createPropertyConverter: TCreateConverter<TCreatePropertyUI, TCreatePropertyDB> = {
  toDb: (data) => {
    return {
      name: data.name,
      address1: data.address1,
      address2: data.address2,
      state: formatToSnakeCase(data.state),
      zip: data.zip,
      defaultPolicyId: data.defaultPolicyId,
      type: data.type,
      city: data.city,
      status: data.state,
      unitCount: data.unitCount,
      unitNumbers: data.unitNumbers,
      userId: data.userId,
      photoUrl: data.photoUrl,
      animalVerificationConfigurationId: data.animalRequirementsSetId,
      accessCodeName: data.accessCodeName,
      isCustomPropertyAllowed: data.isCustomPropertyAllowed,
      accessCodesToAssign: data.accessCodeIds?.length ? data.accessCodeIds : undefined,
    };
  },
};

export const animalTypeManagementConverter: TConvertor<TAnimalTypeManagementUI, TAnimalTypeManagementDB> = {
  fromDb: (data) => ({
    notManagedTypes: data.notManagedTypes,
  }),
  toDb: (data) => ({
    notManagedTypes: data.notManagedTypes,
  }),
};

export const propertyDetailsConverter: TFetchConverter<TPropertiesTypeUI, TPropertiesTypeDB> = {
  fromDb: (data) => {
    return {
      canEditScreenExistingTenantsBeforeOnboarding: data.canEditScreenExistingTenantsBeforeOnboarding,
      isScreeningEnabled: data.isScreeningEnabled,
      notifyExistingApplicants: data.notifyExistingApplicants,
      notifyExistingFutureResidents: data.notifyExistingFutureResidents,
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      id: data.id,
      name: data.name,
      photoUrl: data.photoUrl,
      source: propertySourceConverter.fromDb(data.source),
      state: data.state,
      type: data.type,
      unitCount: data.unitCount,
      zip: data.zip,
      defaultPolicy: {
        ...data.defaultPolicy,
        policyName: data.defaultPolicy?.name,
      },
      petsAllowed: data.defaultPolicy?.petsAllowed,
      propertyStatistics: data.propertyStatistics,
      propertyInternalId: data.propertyInternalId,
      status: data.status,
      unitNumbers: data.unitNumbers,
      userId: data.userId,
      resourceId: data.resourceId,
      isOnboarded: data.isOnboarded,
      lastSyncDate: data.lastSyncDate ? formattedDateTimezone(data.lastSyncDate, 'MM/DD/YYYY HH:mm') : 'N/A',
      paymentSettings: data.paymentSettings,
      lastFileExportDate: data.lastFileExportDate
        ? formattedDateTimezone(data.lastFileExportDate, 'MM/DD/YYYY HH:mm')
        : 'N/A',
      animalRequirementsSetDTO:
        data.animalVerificationConfiguration &&
        propertyConfigSetGetConverter.fromDb(data.animalVerificationConfiguration),
      autoScreeningDaysConfigured: data.autoScreeningDaysConfigured,
      skipAutoScreeningForResidentsAlreadyInRenewalPeriod: data.skipAutoScreeningForResidentsAlreadyInRenewalPeriod,
      screeningConfiguration: data.screeningConfiguration,
      dnaTesting: propertyDnaSettingConverter.fromDb(data.dnaSettings ?? { isEnabled: false, company: null }),
      animalTypeManagement: animalTypeManagementConverter.fromDb(data.animalTypeManagement ?? { notManagedTypes: {} }),
    };
  },
};

export const propertyManagersTableConverter: TFetchConverter<TPropertyManagersTableUI, TPropertyManagersTableDB> = {
  fromDb: (data) => {
    return {
      allowedDelete: data.allowedDelete,
      allowedReInvite: data.allowedReInvite,
      email: data.email,
      name: data.name,
      id: data.id,
      phone: data.phone,
      position: data.position,
      profilePicture: data.profilePicture,
      propertiesCount: data.propertiesCount,
      status: propertyStatusConverter.fromDb(data.status),
      permissions: data.permissions ? userPermissionsFromDb(data.permissions) : undefined,
      assignedNotifications: notificationsFromDb(data.notificationOptions, data.primaryContact),
      animalUpdateConfig: data.animalUpdateConfig ?? null,
    };
  },
};

export const invitePropertyManagerToDb = ({
  data,
  permissions,
  notifications,
  animalUpdateConfig,
}: {
  data: TInvitePropertyManagerUI;
  permissions: TUserPermissionsUI;
  notifications: TNotificationKey[];
  animalUpdateConfig?: TAnimalUpdateConfig | null;
}): TInvitePropertyMangerDB => {
  const { notificationTypes: notificationOptions, isPrimaryContact } = notificationsToDb(notifications);
  const isAnimalActivitySummaryEnabled = notificationOptions?.includes(ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY);
  return {
    email: data.email.trim(),
    name: data.name,
    phone: data.phone ? `+${data.phone}` : undefined,
    position: selectOptionConverter.toDb(data.position),
    properties: data.properties ? data.properties.map((property) => property.value) : undefined,
    accessToFutureProperties: !!data.accessToFutureProperties,
    permissionsUIType: permissions.type,
    ...('setId' in permissions && permissions.setId && { permissionsId: permissions.setId }),
    ...('capabilities' in permissions && { permissions: capabilitiesToDb(permissions.capabilities) }),
    notificationOptions,
    isPrimaryContact,
    animalUpdateConfig: isAnimalActivitySummaryEnabled ? animalUpdateConfig : null,
  };
};

export const addUnitsConverter: TCreateConverter<TAddUnitsUI, TAddUnitsDB> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      unitNumbers: data.unitNumbers,
    };
  },
};

export const propertyManagerPropertiesTableConverter: TFetchConverter<
  TPropertyManagerPropertiesUI,
  TPropertyManagerPropertiesDB
> = {
  fromDb: (data) => {
    return {
      address1: data.address1,
      type: data.type,
      id: data.id,
      unitCount: data.unitCount,
      name: data.name,
      notificationEnabled: data.notificationEnabled,
    };
  },
};

export const unassignedPropertiesConverter: TFetchConverter<TAssignedPropertiesUI, TAssignedPropertiesDB> = {
  fromDb: (data) => {
    return {
      label: data.name,
      value: data.id,
      notificationEnabled: data.notificationEnabled,
    };
  },
};
export const assignedPropertiesConverter: TFetchConverter<TUnassignedPropertiesUI, TUnassignedPropertiesDB> = {
  fromDb: (data) => {
    return {
      label: data.name,
      value: data.id,
    };
  },
};
export const assignePropertiesConverter: TCreateConverter<TAssignPropertyUI, TAssignPropertyDB> = {
  toDb: (data) => {
    return {
      properties: data.properties.map((item) => item.value),
      propertyManagerId: data.propertyManagerId,
    };
  },
};
export const uploadPetChargeStatusesConverter: TCreateConverter<
  TPetChargeStatusesUploadUI,
  TPetChargeStatusesUploadDB
> = {
  toDb: (data) => {
    const formData = new FormData();
    formData.append('integrationType', data.type.value);
    formData.append('propertyId', data.propertyId);
    data.files.forEach((item) => {
      formData.append('file', item);
    });
    return formData;
  },
};
export const propertyUploadTypeConverter: TFetchConverter<EUploadedFileTypeUI, EUploadedFileTypeDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in EUploadedFileTypeDB]: EUploadedFileTypeUI } = {
      [EUploadedFileTypeDB.APPLICANTS]: EUploadedFileTypeUI.APPLICANTS,
      [EUploadedFileTypeDB.RESIDENTS]: EUploadedFileTypeUI.RESIDENTS,
      [EUploadedFileTypeDB.PET_CHARGES]: EUploadedFileTypeUI.PET_CHARGES,
    };
    return dataMapper[data];
  },
};

export const propertyUploadedFilesConverter: TFetchConverter<TPropertyUploadedFilesUI, TPropertyUploadedFilesDB> = {
  fromDb: (dbData) => {
    return {
      id: dbData.id,
      fileUrl: dbData.fileUrl,
      fileName: dbData.fileName,
      type: propertyUploadTypeConverter.fromDb(dbData.type),
      exportTime: dbData.exportTime ? formattedDateTimezone(dbData.exportTime, dateTimeFormat) : 'N/A',
      uploadTime: dbData.uploadTime ? formattedDateTimezone(dbData.uploadTime, dateTimeFormat) : 'N/A',
      uploaderId: dbData.uploaderId,
      uploaderName: dbData.uploaderName,
    };
  },
};

export const residentsScreeningActionConverter: TCreateConverter<
  TResidentsScreeningActionUI,
  TResidentsScreeningActionDB
> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      needToScreen: data.needToScreen,
      notifyExistingApplicants: data.notifyExistingApplicants,
      notifyExistingFutureResidents: data.notifyExistingFutureResidents,
    };
  },
};

export const screeningConfigurationConverter: TCreateConverter<TScreeningConfigurationUI, TScreeningConfigurationDB> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      screeningConfiguration: data.screeningConfiguration,
    };
  },
};

export const notificationConverter: TConvertor<ENotificationTypesUI, ENotificationTypesDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in ENotificationTypesDB]: ENotificationTypesUI } = {
      [ENotificationTypesDB.ANIMAL_APPROVALS_AND_DENIALS]: ENotificationTypesUI.ANIMAL_APPROVALS_AND_DENIALS,
      [ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY]: ENotificationTypesUI.ANIMAL_ACTIVITY_SUMMARY,
      [ENotificationTypesDB.RESIDENT_DOES_NOT_HAVE_ANIMALS]: ENotificationTypesUI.RESIDENT_DOES_NOT_HAVE_ANIMALS,
    };
    return dataMapper[data];
  },
  toDb: (data) => {
    const dataMapper: { [key in ENotificationTypesUI]: ENotificationTypesDB } = {
      [ENotificationTypesUI.RESIDENT_DOES_NOT_HAVE_ANIMALS]: ENotificationTypesDB.RESIDENT_DOES_NOT_HAVE_ANIMALS,
      [ENotificationTypesUI.ANIMAL_ACTIVITY_SUMMARY]: ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY,
      [ENotificationTypesUI.ANIMAL_APPROVALS_AND_DENIALS]: ENotificationTypesDB.ANIMAL_APPROVALS_AND_DENIALS,
    };
    return dataMapper[data];
  },
};

export const propertyMetadataConverter: TFetchConverter<TPropertyMetadataUI, TPropertyMetadataDB> = {
  fromDb: (data) => {
    return {
      name: data.name,
      resourceId: data.id,
      isYardi: data.isYardi,
    };
  },
};

export const assigneTeamMembersConverter: TCreateConverter<TAssignTeamMembersUI, TAssignTeamMembersDB> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      teamMemberIds: data.teamMemberIds.map((item) => item.value),
    };
  },
};

export const teamMembersCsvUploadConverter: TAuthConverter<
  TTeamMembersUploadUI,
  FormData,
  TTeamMembersCSVUploadResultUI,
  TTeamMembersCSVUploadResultDB
> = {
  toDb: (data) => {
    const formData = new FormData();
    data.file.forEach((item) => {
      formData.append('csvFile', new Blob([item], { type: 'application/json' }));
    });
    return formData;
  },
  fromDb: (data) => {
    return {
      totalRowsProcessed: data.totalRowsProcessed ?? 0,
      usersToCreate: data.usersToCreate ?? 0,
      usersToSkip: data.usersToSkip ?? 0,
      usersToDeactivate: data.usersToDeactivate ?? 0,
      usersToUpdate: data.usersToUpdate ?? 0,
    };
  },
};
